import { useState, type FormEvent, type ReactNode } from "react";

interface FeedbackWidgetProps {
  page: string;
  clientId?: number;
}

export function FeedbackWidget({ page, clientId }: FeedbackWidgetProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      const response = await fetch("/api/feedback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page,
          message: message.trim(),
          client_id: clientId ?? null,
        }),
        credentials: "include",
      });
      if (response.ok) {
        setSent(true);
        setMessage("");
        setTimeout(() => {
          setSent(false);
          setOpen(false);
        }, 2000);
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="feedback-widget">
      {!open ? (
        <button
          type="button"
          className="feedback-toggle"
          onClick={() => setOpen(true)}
          title="Send feedback"
        >
          Feedback
        </button>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="feedback-form">
          <div className="feedback-header">
            <strong>Send feedback</strong>
            <button type="button" onClick={() => setOpen(false)}>
              x
            </button>
          </div>
          {sent ? (
            <p className="feedback-thanks">Thanks for your feedback!</p>
          ) : (
            <>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What do you think? What's confusing? What's missing?"
                rows={4}
                disabled={sending}
              />
              <button type="submit" disabled={sending || !message.trim()}>
                {sending ? "Sending..." : "Send"}
              </button>
            </>
          )}
        </form>
      )}
      <style>{`
        .feedback-widget {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          font-family: var(--font-sans, system-ui, sans-serif);
        }
        .feedback-toggle {
          background: var(--color-primary, #0f1729);
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 24px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transition: all 150ms ease;
        }
        .feedback-toggle:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.2);
        }
        .feedback-form {
          background: var(--color-bg, #fff);
          border: 1px solid var(--color-border, #e2e8f0);
          border-radius: 12px;
          padding: 16px;
          width: 320px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12);
        }
        .feedback-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          color: var(--color-text, #0f1729);
          font-size: 14px;
        }
        .feedback-header button {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          color: var(--color-text-muted, #94a3b8);
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 150ms ease;
        }
        .feedback-header button:hover {
          background: var(--color-bg-muted, #f1f5f9);
          color: var(--color-text, #0f1729);
        }
        .feedback-form textarea {
          width: 100%;
          border: 1px solid var(--color-border, #e2e8f0);
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          resize: vertical;
          box-sizing: border-box;
          color: var(--color-text, #0f1729);
          background: var(--color-bg, #fff);
          transition: border-color 150ms ease, box-shadow 150ms ease;
          line-height: 1.5;
        }
        .feedback-form textarea:focus {
          outline: none;
          border-color: var(--color-accent, #0ea5e9);
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.08);
        }
        .feedback-form button[type="submit"] {
          margin-top: 10px;
          background: var(--color-accent, #0ea5e9);
          color: #fff;
          border: none;
          padding: 8px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 150ms ease;
        }
        .feedback-form button[type="submit"]:hover {
          background: var(--color-accent-hover, #0284c7);
        }
        .feedback-form button[type="submit"]:disabled {
          background: var(--color-border, #e2e8f0);
          color: var(--color-text-muted, #94a3b8);
          cursor: not-allowed;
        }
        .feedback-thanks {
          color: var(--color-success, #16a34a);
          text-align: center;
          padding: 24px 0;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}
